// Customer-facing view model for the live Robin chat surface.
//
// Backend rows are normalized into this small UI contract before they reach
// React components. Keeping only the live choices card here prevents stale local
// flows from leaking back into the Postgres-backed surface.

import type {
  AgentConversationAttachment,
  AgentMutationReceipt,
} from "./api/agent-conversation-client"

export type RobinRole = "user" | "assistant"

/** Conversation-level status, used for the sidebar and Robin's header state. */
export type RobinConversationStatus =
  | "idle"
  | "working"
  | "needs_you"
  | "done"
  | "failed"

export type RobinConversation = {
  id: string
  title: string
  status: RobinConversationStatus
  /** Short relative label, e.g. "2m", "1h", "Yesterday". */
  updatedLabel: string
  messages: RobinMessage[]
  /** When set, Robin is actively thinking; see `robinStateFor`. */
  pending?: RobinPending | null
  /** True once the backend transcript has been loaded into local state. */
  hydrated?: boolean
  /** Backend conversations should show in the sidebar before hydration. */
  hasHistory?: boolean
  /** The backend ask currently owned by the composer, not by transcript cards. */
  activeAsk?: RobinActiveAsk | null
}

export type RobinPending = {
  /**
   * Thinking trace shown while the backend is working. Empty is allowed: Robin
   * still enters the thinking state while the first progress event is in flight.
   */
  steps: string[]
}

export type RobinMutationReceipt = AgentMutationReceipt
export type RobinFileAttachment = AgentConversationAttachment & {
  localPreviewUrl?: string
}

export type RobinMessage = {
  id: string
  role: RobinRole
  text?: string
  attachment?: RobinAttachment
  fileAttachments?: RobinFileAttachment[]
  receipt?: RobinMutationReceipt
  /**
   * While present, the assistant message is visually "writing" and Robin uses
   * the responding avatar frame. Clearing it hands the state back to idle/waiting.
   */
  streamChars?: number
}

export type RobinAttachment = RobinChoicesAttachment

/**
 * Backend handle for a live ask_user card. The card needs only enough
 * information to answer the ask through the conversation message stream.
 */
export type LiveContextRef = {
  conversationId: string
  askUserToolCallId: string
  kind: "candidate" | "job" | "other"
}

export type RobinActiveAsk = {
  conversationId: string
  askUserToolCallId: string
  kind: "candidate" | "job" | "other"
  options: RobinChoice[]
}

export type RobinChoicesAttachment = {
  type: "choices"
  id: string
  /** The chosen option id once resolved. For live cards this is the option index. */
  resolved?: string
  options: RobinChoice[]
  /** Historical/suppressed cards are display-only. */
  readOnly?: boolean
  /** Present only while this card can still answer a backend ask. */
  live?: LiveContextRef
}

export type RobinChoice = {
  /** Backend ask option index string. It is converted back to a number on click. */
  id: string
  name: string
  /** Light context line under the label. */
  meta: string
}

export const CONVERSATION_STATUS_META: Record<
  RobinConversationStatus,
  { label: string; dotClass: string; pulse?: boolean }
> = {
  idle: { label: "Draft", dotClass: "bg-muted-foreground/40" },
  working: { label: "Working", dotClass: "bg-primary", pulse: true },
  needs_you: { label: "Needs you", dotClass: "bg-attention" },
  failed: { label: "Retry needed", dotClass: "bg-destructive" },
  done: { label: "Done", dotClass: "bg-success" },
}
